import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import { metaByIdFromDefs } from "../../helpers/scoreAggregate";
import { companyMonthStr, companyToday } from "../../helpers/companyTime";
import { filterKpisForPublicDisplay, filterLiveKpis } from "../../constants/kpi";
import {
  UseCompanySubWorkspaces,
  UseCompanyWorkspaces,
  buildYetiScoresForKpis,
  windowUsesRangeYeti,
  yetiBucketForWindow,
} from "../../hooks/company";
import { climbFracs } from "../BeatTheYeti/pace";
import {
  fetchWorkspaceScores,
  monthsForWindow,
  resolveWindow,
  sumScores,
} from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";

/**
 * Every department's own metrics on one board, each one green or red on
 * whether that workspace is beating the Yeti.
 *
 * A row is a WORKSPACE and the cells are THAT workspace's own metrics — not a
 * shared set of columns. Two departments rarely track the same things, and
 * forcing them into shared columns would fill the grid with blanks for every
 * metric a team doesn't have. So each row carries its own metric names and the
 * columns are only positions.
 *
 * Sub-workspaces are rows in their own right, listed under their parent and
 * indented. A parent is not a heading: it is a workspace like any other and
 * shows whatever metrics IT tracks — which, for a pure grouping workspace, is
 * often none, and the row says so rather than drawing an empty band.
 *
 * ── Beating the Yeti ──────────────────────────────────────────────────────
 * The Yeti's pace comes from the same builder the Beat the Yeti card uses
 * (buildYetiScoresForKpis), read at the bucket that matches this card's window
 * (yetiBucketForWindow). Beating is decided in this order:
 *
 *   1. POINTS, when the Yeti has any this window — playerPts >= yetiPts. The
 *      rule the Yeti card itself uses, so the two never disagree.
 *   2. AMOUNT, when he has none. Anchor and whole-month metrics are pinned to
 *      0 points in the day/week/custom buckets on purpose (they would otherwise
 *      pad the rank), and `playerPts >= 0` is true for everyone — which would
 *      paint a whole column green for nothing. Those fall back to the amount
 *      against the Yeti's own pace amount, respecting a metric whose direction
 *      says lower is better.
 *   3. NOTHING, when he has neither points nor a pace amount. A metric with no
 *      target has no Yeti to beat; the cell reads grey and says so. Calling
 *      that a win is the one answer that would be a lie.
 */

const rid = () => `d${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** How many metric cells a row can show. Five is the sketch; the rest is room. */
export const MAX_COLS = 8;
export const DEFAULT_COLS = 5;

export const colsOf = (s) => {
  const n = Math.round(num(s?.maxCols));
  if (!n) return DEFAULT_COLS;
  return Math.max(1, Math.min(MAX_COLS, n));
};

/** The workspaces the card was pointed at, in the order they were picked. */
export const pickedWorkspaceIds = (s) =>
  (Array.isArray(s?.workspaceIds) ? s.workspaceIds : []).filter(Boolean).map(String);

/* Undefined reads as "on": every card saved before this setting existed
   already listed sub-workspaces, and a default of off would silently empty
   half the rows off an existing board. Only consulted for a card still on
   the legacy all-or-nothing toggle — see subsExplicit below. */
export const includesSubWorkspaces = (s) => s?.includeSubWorkspaces !== false;

/* Off (the default) means `workspaceIds` is read the OLD way: any picked
   top-level workspace auto-expands to every one of ITS sub-workspaces right
   now, dynamically, per includesSubWorkspaces above — so a company that never
   opens the new picker keeps behaving exactly as it always has, including
   picking up a sub-workspace added after the fact.
   On means `workspaceIds` is taken completely literally, at whatever mix of
   top-level and sub-workspace ids it holds, with no expansion at all — set
   the instant Setup's picker is used to add or remove ANY single workspace
   (parent or sub) on its own, which is the one thing the all-or-nothing
   toggle could never do. */
export const subsExplicit = (s) => !!s?.subsExplicit;

/** A row's hand-picked metric ids, or [] when it is left on automatic. */
export const metricsForWorkspace = (s, wsId) => {
  const map = s?.metricsByWs;
  const list = map && typeof map === "object" ? map[String(wsId)] : null;
  return Array.isArray(list) ? list.filter(Boolean).map(String) : [];
};

export const isAutoRow = (s, wsId) => metricsForWorkspace(s, wsId).length === 0;

/**
 * The rows a card resolves to: each picked workspace, then its sub-workspaces
 * right beneath it.
 *
 * Deduped, because a sub-workspace picked by hand AND pulled in by its parent
 * is still one department — listing it twice would double it in the "beating"
 * count at the top of the card. First mention wins, so an explicit pick keeps
 * the position the admin put it in.
 */
export const resolveRows = (settings, workspaces, subWsByParent) => {
  const byId = {};
  (workspaces || []).forEach((w) => { byId[String(w._id)] = w; });

  const picked = pickedWorkspaceIds(settings);
  const subs = includesSubWorkspaces(settings);
  const explicit = subsExplicit(settings);
  /* Nothing picked yet is not an error — a card dropped on a board should show
     something real immediately, so it falls back to every top-level workspace
     the company has. But once Setup's picker has been used (explicit) and an
     admin unticks every last one, that IS a real choice — an empty board, not
     "start over" — so it stays empty instead of springing back to automatic. */
  let seedIds;
  if (picked.length) seedIds = picked;
  else if (explicit) seedIds = [];
  else seedIds = (workspaces || []).filter((w) => !w.parentId).map((w) => String(w._id));
  const out = [];
  const seen = new Set();
  const push = (wsId, depth) => {
    const id = String(wsId);
    if (seen.has(id) || !byId[id]) return;
    seen.add(id);
    out.push({ key: id, workspaceId: id, name: byId[id].name || "Workspace", depth });
  };

  seedIds.forEach((id) => {
    const ws = byId[String(id)];
    const depth = ws?.parentId ? 1 : 0;
    push(id, depth);
    /* Custom mode (subsExplicit) takes `workspaceIds` completely literally —
       whatever mix of parents and subs is in there is the whole list, with no
       further expansion. Legacy/automatic mode keeps expanding a picked
       TOP-LEVEL workspace to every one of its current children, dynamically,
       exactly as it always has — a picked SUB-workspace has none of its own
       to expand either way. */
    if (explicit || depth !== 0 || !subs) return;
    (subWsByParent?.[String(id)] || []).forEach((child) => push(child._id, 1));
  });
  return out;
};

/* ── Which metrics a row shows ─────────────────────────────────────────────
   Automatic means the workspace's own LIVE set — the very metrics already on
   its dashboard, in its own order, so a row matches what that team sees when
   they open their board. Scorekeeper-only metrics are dropped the same way
   every other public surface drops them (this card goes on TVs). */
export const autoMetricsFor = (defs, cap) =>
  filterLiveKpis(filterKpisForPublicDisplay((defs || []).filter((k) => k && !k.isDraft && !k.disabled)), cap);

/**
 * The full pickable catalog for a workspace.
 *
 * Deliberately NOT filterLiveKpis: that caps a workspace at five "Live"
 * metrics, and anything outside those five would simply be unpickable here —
 * the exact trap the Payroll vs Revenue hours picker had to avoid.
 */
export const pickableMetricsFor = (defs) =>
  (defs || []).filter((k) => k && !k.isDraft && !k.disabled);

/* ── Beating ─────────────────────────────────────────────────────────────── */

export const lowerIsBetter = (kpi) => (kpi?.direction ?? 1) < 0;

/**
 * @returns {{ beating: boolean|null, basis: "points"|"amount"|null }}
 *   `null` means there is no Yeti pace on this metric at all — see the header.
 */
export const beatStatus = ({ playerPts, yetiPts, playerAmt, yetiAmt, kpi }) => {
  if (num(yetiPts) > 0) return { beating: num(playerPts) >= num(yetiPts), basis: "points" };
  if (num(yetiAmt) > 0) {
    const lower = lowerIsBetter(kpi);
    return { beating: lower ? num(playerAmt) <= num(yetiAmt) : num(playerAmt) >= num(yetiAmt), basis: "amount" };
  }
  return { beating: null, basis: null };
};

export const emptyRowKey = rid;

/**
 * @param {object}  settings  the card's saved options
 * @param {string}  bucket    today | week | month | lastMonth | quarter | ytd | custom
 * @param {object} [custom]   { from, to } when bucket is "custom"
 */
export default function useDeptMetrics(settings, bucket = "month", custom = null) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const holidays = useSelector((s) => s.companyReducer.holidays);
  const workspaces = UseCompanyWorkspaces(true) || [];
  const subWsByParent = UseCompanySubWorkspaces();

  const cols = colsOf(settings);

  const rows = useMemo(
    () => resolveRows(settings, workspaces, subWsByParent),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(pickedWorkspaceIds(settings)),
      includesSubWorkspaces(settings),
      subsExplicit(settings),
      JSON.stringify((workspaces || []).map((w) => [w._id, w.name, w.parentId])),
      subWsByParent,
    ],
  );

  const wsIds = useMemo(() => rows.map((r) => r.workspaceId), [rows]);
  const wsKey = wsIds.join(",");

  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [scoresByWs, setScoresByWs] = useState({});
  const [loadedKey, setLoadedKey] = useState("");

  const win = bucket === "year" ? "ytd" : bucket;
  const months = useMemo(
    () => monthsForWindow(win, custom),
    [win, customKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const monthsKey = months.join(",");
  const fetchKey = `${companyId || ""}|${wsKey}|${monthsKey}`;
  const fetching = !!wsIds.length && loadedKey !== fetchKey;
  const hasScores = Object.keys(scoresByWs).length > 0;

  useEffect(() => {
    if (!companyId || !wsIds.length) {
      setScoresByWs({});
      setLoadedKey(fetchKey);
      return undefined;
    }
    let alive = true;
    Promise.all(
      wsIds.map((id) => fetchWorkspaceScores(companyId, id, months)
        .then((s) => [id, s])
        .catch(() => [id, []])),
    ).then((pairs) => {
      if (!alive) return;
      setScoresByWs(Object.fromEntries(pairs));
      setLoadedKey(fetchKey);
    });
    return () => { alive = false; };
  }, [companyId, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    const { from, to } = resolveWindow(win, custom);
    const yetiBucket = yetiBucketForWindow(win);
    const rangeYeti = windowUsesRangeYeti(win);

    /* The Yeti is built per workspace from that workspace's own metric
       configs, with the same month reference / as-of / range arguments
       useWorkspaceData passes — a quarter or a custom range paces him over the
       business days in THAT range, not month-to-date. */
    const yetiFor = (defs) => {
      if (!defs.length) return null;
      const monthRef = rangeYeti && from?.isValid?.() ? from.format("YYYY-MM") : companyMonthStr();
      const asOf = rangeYeti && to?.isValid?.()
        ? moment.min(to.clone().endOf("day"), companyToday().endOf("day"))
        : companyToday();
      const range = rangeYeti && from?.isValid?.()
        ? { from: from.format("YYYY-MM-DD"), to: (to?.isValid?.() ? to : from).format("YYYY-MM-DD") }
        : null;
      return buildYetiScoresForKpis(defs, monthRef, holidays, asOf, range);
    };

    const out = rows.map((row) => {
      const defs = (companyKpis && companyKpis[row.workspaceId]) || [];
      const { perKpi } = sumScores(scoresByWs[row.workspaceId], from, metaByIdFromDefs(defs), to);
      const yeti = yetiFor(defs);
      const pace = yeti?.[yetiBucket] || yeti?.month || null;

      const picked = metricsForWorkspace(settings, row.workspaceId);
      /* A hand-picked list is resolved against the workspace's live catalog
         every render rather than trusted as-is — a metric deleted, disabled or
         turned into a draft since it was picked must drop out of the row, not
         sit there reading a dead id forever. */
      const chosen = picked.length
        ? picked.map((id) => defs.find((k) => String(k._id) === String(id))).filter(Boolean)
        : autoMetricsFor(defs, cols);

      const cells = chosen.slice(0, cols).map((kpi) => {
        const id = String(kpi._id);
        const playerAmt = num(perKpi[id]?.amt);
        const playerPts = num(perKpi[id]?.pts);
        const yetiAmt = num(pace?.[id]?.amt);
        const yetiPts = num(pace?.[id]?.pts);
        const { beating, basis } = beatStatus({ playerPts, yetiPts, playerAmt, yetiAmt, kpi });
        /* Where the two dots sit on the climb — the SAME rule Beat the Yeti's
           own tiles use (BeatTheYeti/pace.js), so one metric can never read as
           two different races on two cards of the same board. */
        const fracs = climbFracs({
          kpi, playerAmt, yetiAmt, bucket: yetiBucket, totalDays: yeti?.totalDays,
        });
        return {
          kpiId: id,
          name: kpi.title || kpi.name || "Metric",
          kind: kpi.kind,
          target: num(kpi.target),
          lower: lowerIsBetter(kpi),
          playerAmt,
          playerPts,
          yetiAmt,
          yetiPts,
          diff: playerAmt - yetiAmt,
          playerFrac: fracs.player,
          yetiFrac: fracs.yeti,
          beating,
          basis,
        };
      });

      const scored = cells.filter((c) => c.beating !== null);
      return {
        ...row,
        cells,
        auto: picked.length === 0,
        beating: scored.filter((c) => c.beating).length,
        scored: scored.length,
        /* A grouping workspace tracks nothing of its own. Saying so on the row
           is the honest version of the blank band. */
        empty: cells.length === 0,
      };
    });

    const allScored = out.reduce((s, r) => s + r.scored, 0);
    const allBeating = out.reduce((s, r) => s + r.beating, 0);

    return {
      rows: out,
      cols,
      beating: allBeating,
      scored: allScored,
      loading: fetching && !hasScores,
      refreshing: fetching && hasScores,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows,
    cols,
    JSON.stringify(settings?.metricsByWs || {}),
    scoresByWs,
    companyKpis,
    holidays,
    win,
    customKey,
    fetching,
    hasScores,
  ]);
}
